/**
 * Public social endpoints — the article pages and the share buttons.
 *
 * WHAT: typed wrappers over `/api/social/public/*` (published articles),
 * `/api/social/share-links` (a tracked short link per target and
 * platform) and `/api/social/share-events` (a share button was used).
 * None of them needs a login.
 *
 * WHY: one module owns the payload shapes so the article pages and the
 * share sheet cannot drift from the backend contract
 * (backend/app/api/v1/endpoints/social.py, "Public" section). Responses
 * come back already unwrapped from `{ success, data }` by `api.*`.
 */

import { api } from '@/shared/api/apiClient';
import type { ShareTarget, SharePlatform } from '@/utils/share';

export interface PublicPostListItem {
    slug: string;
    title: string | null;
    subtitle: string | null;
    cover_image_url: string | null;
    published_at: string | null;
    excerpt: string;
}

export interface PublicPostList {
    items: PublicPostListItem[];
    total: number;
}

/** The listing an article is about, as `listing_share.share_summary` describes it. */
export interface PublicPostListingSummary {
    listing_id: string;
    title: string;
    address: string;
    price: string;
    beds: string;
    baths: string;
    sqft: string;
    photo: string;
    url: string;
}

export interface PublicPostOpenHouse {
    id: number;
    title: string | null;
    full_address: string | null;
    starts_at: string | null;
    ends_at: string | null;
    schedule_label: string;
    url: string;
}

export type PublicPostTopicType = 'article' | 'property' | 'open_house' | 'general';

export interface PublicPost {
    slug: string;
    title: string | null;
    subtitle: string | null;
    /** Rendered from markdown and sanitised server-side; safe to inject. */
    body_html: string;
    cover_image_url: string | null;
    published_at: string | null;
    /** Where the article sends readers: the buyer's agreement by default. */
    cta_url: string;
    topic_type: PublicPostTopicType;
    listing_summary: PublicPostListingSummary | null;
    open_house: PublicPostOpenHouse | null;
}

export interface ShareLink {
    code: string;
    url: string;
}

/** Exactly one of the three targets is set. */
export interface ShareTargetBody {
    listing_id?: string;
    open_house_id?: number;
    post_slug?: string;
}

export interface ShareLinkRequest extends ShareTargetBody {
    platform: SharePlatform;
}

export interface ShareEventRequest extends ShareTargetBody {
    platform: SharePlatform;
    /** Where on the site the button sits ("card", "stacked", "inline"...). */
    src?: string;
}

export const PUBLIC_POSTS_PAGE_SIZE = 12;

/** The request body naming a share target, as the backend wants it. */
export function shareTargetBody(target: Pick<ShareTarget, 'kind' | 'id'>): ShareTargetBody {
    switch (target.kind) {
        case 'listing':
            return { listing_id: String(target.id) };
        case 'open_house':
            return { open_house_id: Number(target.id) };
        case 'post':
            return { post_slug: String(target.id) };
    }
}

export const socialPublicApi = {
    listPosts(limit: number = PUBLIC_POSTS_PAGE_SIZE, skip: number = 0): Promise<PublicPostList> {
        return api.get<PublicPostList>('/social/public/posts', { limit, skip });
    },

    getPost(slug: string): Promise<PublicPost> {
        return api.get<PublicPost>(`/social/public/posts/${encodeURIComponent(slug)}`);
    },

    /** Idempotent per (target, platform): the same link comes back every time. */
    mintShareLink(body: ShareLinkRequest): Promise<ShareLink> {
        return api.post<ShareLink>('/social/share-links', body);
    },

    recordShareEvent(body: ShareEventRequest): Promise<{ recorded: boolean }> {
        return api.post<{ recorded: boolean }>('/social/share-events', body);
    },
};

export default socialPublicApi;
